/**
 * Typed message contract between `createAudioSidecar` (main thread) and
 * `sidecarDemux.worker.ts`. Mirrors `workers/s3UploadMessages.ts`.
 */
import type { DemuxOptions, SidecarOutcome } from './types';

export interface SidecarDemuxRequest {
  type: 'demux';
  id: string;
  file: File;
  options?: Pick<
    DemuxOptions,
    'chunkBytes' | 'maxSidecarRatio' | 'allowCo64Over4GiB'
  >;
}

export interface SidecarDemuxCancel {
  type: 'cancel';
  id: string;
}

export type SidecarDemuxInbound = SidecarDemuxRequest | SidecarDemuxCancel;

export interface SidecarDemuxResult {
  type: 'result';
  id: string;
  outcome: SidecarOutcome;
}

export type SidecarDemuxOutbound = SidecarDemuxResult;

export function isSidecarDemuxResult(
  data: unknown,
): data is SidecarDemuxResult {
  if (typeof data !== 'object' || data === null) return false;
  if (!('type' in data) || !('id' in data) || !('outcome' in data))
    return false;
  return (
    data.type === 'result' &&
    typeof data.id === 'string' &&
    typeof data.outcome === 'object' &&
    data.outcome !== null
  );
}

/** The slice of `Worker` the main-thread wrapper uses; tests inject a fake. */
export interface SidecarWorkerLike {
  postMessage(message: SidecarDemuxInbound): void;
  addEventListener(
    type: 'message',
    listener: (event: { data: unknown }) => void,
  ): void;
  addEventListener(
    type: 'error',
    listener: (event: { message?: string }) => void,
  ): void;
  terminate(): void;
}
